import { ReactNode } from 'react';

import { SubscriptionPlanType } from '@repo/subscription/api';

import useFreeTrial, { FreeTrialData } from '../../../../hooks/useFreeTrial';
import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../../stores/planSelectionStore';
import { capitalizeFirstLetter } from '../../../../utils/string';
import {
  SubscriptionChangePreviewData,
  SubscriptionHookParams,
} from '../../hooks/types';
import useSubscriptionChangeState from '../../hooks/useSubscriptionChangeState';
import { isYearlySubscription } from '../../utils';

interface PaymentDetailsData {
  tag?: string;
  title: string;
  note?: ReactNode;
  warning?: string;
  isUpgrade: boolean;
  isChanged?: boolean;
}

interface PlanPurchaseDetailsParams extends SubscriptionHookParams {
  subscriptionChangePreviewData: SubscriptionChangePreviewData;
}

const CANCELED_NOTE =
  'Please note: Your subscription is still active until the next payment cycle. You can renew your subscription at any time.';
const DOWNGRADE_NOTE: ReactNode = (
  <>
    Please note: By downgrading your subscription plan, you might{' '}
    <a
      href="https://www.kittl.com/help/subscription/downgrading-your-subscription"
      rel="noreferrer"
      target="_blank"
    >
      lose access
    </a>{' '}
    to certain features or premium content.
  </>
);

/**
 * Gives a descriptive title for a plan indicating whether it is an upgrade from the current plan or a change in the current plan.
 */
const getTitle = ({
  currentPlan,
  selectedPlan,
  intervalChanged,
  freeTrial,
  yearly,
}: {
  currentPlan?: SubscriptionPlanType;
  selectedPlan: SubscriptionPlanType;
  intervalChanged: boolean;
  freeTrial: FreeTrialData;
  yearly: boolean;
}): string => {
  const planName = capitalizeFirstLetter(selectedPlan.toLowerCase());
  let title = `${planName} plan`;

  if (!currentPlan && (freeTrial.isRunning || freeTrial.isAvailable)) {
    return title;
  } else if (selectedPlan !== currentPlan) {
    const action =
      !currentPlan || selectedPlan === SubscriptionPlanType.EXPERT
        ? 'Upgrade'
        : 'Change';

    title = `${action} to ${planName} plan`;
  } else if (intervalChanged) {
    title = `Change to ${yearly ? 'yearly' : 'monthly'} billing`;
  }

  return title;
};

/**
 * This hook gets the details for purchasing a subscription plan.
 *
 * It returns a descriptive title to be displayed for the plan being purchased, specifies if it is an upgrade or not.
 * Also returns a tag or note if applicable to give useful information about the subscription.
 */
const usePlanPurchaseDetails = ({
  currentSubscription,
  subscriptionChangePreviewData,
}: PlanPurchaseDetailsParams): PaymentDetailsData => {
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);

  const freeTrial = useFreeTrial();
  const isCanceledFreeTrial = freeTrial.isCancelled;

  const planChangeState = useSubscriptionChangeState({ currentSubscription });

  const isSwitchingToMonthly =
    isYearlySubscription(currentSubscription) && !yearly;

  const isCanceled = !!currentSubscription?.canceledAt;

  const isSwitchingFromExpertToPro =
    currentSubscription?.plan?.name === SubscriptionPlanType.EXPERT &&
    !!planChangeState?.planChanged;

  const isDowngrading = isSwitchingFromExpertToPro || isSwitchingToMonthly;

  const isCanceledFreeTrialUpgrade = isCanceledFreeTrial && !isDowngrading;

  const isUpgrade =
    isCanceledFreeTrialUpgrade || (!isCanceled && !isDowngrading);

  const result: PaymentDetailsData = {
    title: getTitle({
      currentPlan: currentSubscription?.plan?.name,
      selectedPlan: plan,
      intervalChanged: !!planChangeState?.intervalChanged,
      freeTrial,
      yearly,
    }),
    isUpgrade,
    isChanged: !!planChangeState,
  };

  if (!planChangeState && isCanceled) {
    result.note = CANCELED_NOTE;
  } else if (isDowngrading) {
    result.note = DOWNGRADE_NOTE;
  }

  if (freeTrial.isAvailable || (freeTrial.isRunning && !planChangeState)) {
    result.tag = 'TRIAL';
  } else if (!planChangeState) {
    result.tag = 'ACTIVE';
  }

  if (subscriptionChangePreviewData.missingPaymentMethod) {
    result.warning = 'Please update your payment information to continue. ';
  }

  return result;
};

export default usePlanPurchaseDetails;
